import React, { useLayoutEffect, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import i18n, { use } from 'i18next';
import { useTranslation } from "react-i18next";
import { ConfigProvider, Menu, Pagination, Tooltip } from 'antd';
import '../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import useAdminApplicationDetailStore from "../store/useAdminApplicationDetailStore";
import useAdminApplicationStore from "../store/useAdminApplicationStore";
import useAuthAdminStore from "../store/useAuthAdminStore";
import useApplyStore from "../store/useApplyStore";
import Flex from "../components/core/Flex";
import ShadowBox from "../components/core/ShadowBox";
import HeaderBar from "../components/admin/HeaderBar";
import useViewStore from "../store/useViewStore";
import TaskListBar from "../components/admin/application/TaskListBar";
import ApplicationInfo from "../components/admin/application/ApplicationInfo";
import Claim from "../components/admin/application/Claim";
import Assessment from "../components/admin/application/Assessment";
import RequestStatus from "../components/admin/application/RequestStatus";
import Details from "../components/admin/application/Details";
import Documents from "../components/admin/application/Documents";
import Email from "../components/admin/application/Email";
import FormHeader from "../components/form/FormHeader";
import { apiDomain, basename } from "../setting";
import withErrorHandling from "../function/withErrorHandling";

function PageAdminApplicationDetail() {
  const navigate = useNavigate();
  const { isLargeDesktopView } = useViewStore();
  const { firstName, lastName, role, isCSTeam } = useAuthAdminStore();
  const { getHospitalList } = useApplyStore();
  const { t } = useTranslation();
  const { aid } = useParams();

  const { data } = useAdminApplicationDetailStore();
  const { getPreAuthStaff } = useAdminApplicationStore();
  const { selectedTab, setSelectedTab, resetAllPopupValue } = useAdminApplicationDetailStore();
  const { detailRedPoint, documentRedPoint } = useAdminApplicationDetailStore();
  const { setDetailRedPoint, setDocumentRedPoint } = useAdminApplicationDetailStore();
  const { admNotedPreAuthForm } = useAdminApplicationDetailStore();

  const isClaimService = data?.form?.intstatus === "APPROVELOG" || data?.form?.intstatus === "PENDCHARGE" || data?.form?.intstatus === "OUTCHARGE" || data?.form?.intstatus === "SCHCHARGE" || data?.form?.intstatus === "CHARGESETTLE" || data?.form?.intstatus === "CLAIMSETTLE";
  const isLOG = data?.form?.flowType === "LOG" && !isClaimService;
  const isPCA = data?.form?.flowType === "PCA" || data?.form?.flowType === "CHGTOPCA";
  const isSTP = data?.form?.flowType === "STP";
  const isAllRequest = !isLOG && !isPCA && !isSTP && !isClaimService;
  // const isCSTeam = role === "CSADM" || role === "CSVIEW";

  const loadGetHospitalList = withErrorHandling(getHospitalList);
  const loadGetPreAuthStaff = withErrorHandling(getPreAuthStaff);
  const loadAdmNotedPreAuthForm = withErrorHandling(admNotedPreAuthForm);

  useEffect(() => {
    loadGetHospitalList();
    loadGetPreAuthStaff();
    resetAllPopupValue();
  }, []);

  useLayoutEffect(() => {
    if (isClaimService && !isCSTeam()) {
      setSelectedTab("claim");
    } else if (data?.form?.intstatus === "APPROVEPCA") {
      setSelectedTab("assessment");
    }
  }, [isClaimService, data?.form?.intstatus]);

  const items = [
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'assessment'}>{t("translation:admin.request.tab.assessment.title")}</MenuItemText><MenuItemCount $active={selectedTab === 'assessment'}></MenuItemCount></MenuItem>,
      key: 'assessment',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'claim'}>{t("translation:admin.request.tab.claim.title")}</MenuItemText><MenuItemCount $active={selectedTab === 'claim'}></MenuItemCount></MenuItem>,
      key: 'claim',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'status'}>{t("translation:admin.request.tab.request_status.title")}</MenuItemText><MenuItemCount $active={selectedTab === 'status'}></MenuItemCount></MenuItem>,
      key: 'status',
    },
    {
      label: <MenuItem>{detailRedPoint > 0 && <PointRed />}<MenuItemText $active={selectedTab === 'details'}>{t("translation:admin.request.tab.details")}</MenuItemText><MenuItemCount $active={selectedTab === 'details'}></MenuItemCount></MenuItem>,
      key: 'details',
    },
    {
      label: <MenuItem>{documentRedPoint > 0 && <PointRed />}<MenuItemText $active={selectedTab === 'documents'}>{t("translation:admin.request.tab.document")}</MenuItemText><MenuItemCount $active={selectedTab === 'documents'}></MenuItemCount></MenuItem>,
      key: 'documents',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'email'}>{t("translation:admin.request.tab.email.title")}</MenuItemText><MenuItemCount $active={selectedTab === 'email'}></MenuItemCount></MenuItem>,
      key: 'email',
    },
  ].filter(item => (item.key === "assessment" && data?.form?.intstatus === "APPROVEPCA") || (item.key === "claim" && isClaimService) || item.key === "status" || item.key === "details" || item.key === "documents" || item.key === "email");

  return (
    <PageApplicationDetail>
      <HeaderBar title={t("translation:admin.portal_name")} name={`${firstName} ${lastName}`} role={role} />

      <Container>
        <MainContainer>
          <Flex>
            <a href={`/${basename}/${i18n.language}/admin/application`}>
              <FormHeader style={{ marginBottom: 0 }}>{t("translation:admin.request.list_title")}</FormHeader>
            </a>
            <Icon src={`${process.env.PUBLIC_URL}/images/arrow_r.png`} alt="next" />

            {isLOG && <FormHeader style={{ marginBottom: 0 }}>{t("translation:admin.request.tab.preAuth")}</FormHeader>}
            {isPCA && <FormHeader style={{ marginBottom: 0 }}>{t("translation:admin.request.tab.PCA")}</FormHeader>}
            {isSTP && <FormHeader style={{ marginBottom: 0 }}>{t("translation:admin.request.tab.STP")}</FormHeader>}
            {isClaimService && <FormHeader style={{ marginBottom: 0 }}>{t("translation:admin.request.tab.claim_service")}</FormHeader>}
            {isAllRequest && <FormHeader style={{ marginBottom: 0 }}>{t("translation:admin.request.tab.all")}</FormHeader>}
          </Flex>

          <ApplicationInfo />

          <MenuContainer>
            <Menu onClick={(e) => {
              setSelectedTab(e.key);

              if (e.key === "details" && detailRedPoint > 0) {
                setDetailRedPoint("0");
                loadAdmNotedPreAuthForm(aid);
              }
            }} selectedKeys={[selectedTab]} mode="horizontal" items={items} style={{ minWidth: 0, flex: "auto" }} />
            <MenuExtend></MenuExtend>
          </MenuContainer>

          {selectedTab === 'claim' && <ApplicationMain><Claim /></ApplicationMain>}
          {selectedTab === 'assessment' && <ApplicationMain><Assessment /></ApplicationMain>}
          {selectedTab === 'status' && <ApplicationMain><RequestStatus /></ApplicationMain>}
          {
            selectedTab === 'details' && <>
              {
                isLargeDesktopView ?
                  <ApplicationMainContainer>
                    <ApplicationMain><Details /></ApplicationMain>
                    <ApplicationMain><Documents /></ApplicationMain>
                  </ApplicationMainContainer>
                  :
                  <ApplicationMain><Details /></ApplicationMain>
              }
            </>

          }
          {selectedTab === 'documents' && <ApplicationMain><Documents /></ApplicationMain>}
          {selectedTab === 'email' && <ApplicationMain><Email /></ApplicationMain>}
        </MainContainer>

        <TaskListBar />
      </Container>
    </PageApplicationDetail >
  );
}

const PageApplicationDetail = styled(Flex)`
  width: 100%;
  height: 100%;
  flex-direction: column;
  gap: 0px;
`;

const Container = styled(Flex)`
  width: 100%;
  height: 100%;
  background-color: var(--aia-bx-gray-10);
  align-items: flex-start;
  gap: 0px;
`;

const MainContainer = styled(Flex)`
  width: 100%;
  height: calc(100vh - 66px);
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px 36px 24px;
  gap: 24px;
`;

const MenuContainer = styled(Flex)`
  width: 100%;
  align-items: flex-end;
  gap: 0;
`;

const MenuItem = styled.div`
  display: flex;
  margin: 0px 24px;
  flex-direction: row;
  gap: 8px;
`;

const MenuItemText = styled.div`
  font-size: 14px;
  color: var(--aia-bx-dark-blue);
  text-shadow: 0 0 0 transparent;
  font-weight: normal;
  transition: font-weight 0.3s ease;
  ${(props) => props.$active && `font-weight: bold;`}
`;

const MenuItemCount = styled.div`
  color: var(--aia-bx-blue-2);
  text-shadow: 0 0 0 transparent;
  font-weight: normal;
  transition: font-weight 0.3s ease;
  ${(props) => props.$active && `font-weight: bold;`}
`;

const MenuExtend = styled.div`
  line-height: 46px;
  border: 0;
  border-bottom: 1px solid rgba(5, 5, 5, 0.06);
  box-shadow: none;
`;

const ApplicationMain = styled(ShadowBox)`
  overflow-y: auto;
  height: 100%;
`;

const ApplicationMainContainer = styled(Flex)`
  align-items: flex-start;
  width: 100%;
  overflow-y: auto;
  padding: 5px;
  margin: -5px;
  width: calc(100% + 10px);
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
`;

const PointRed = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--aia-bx-aiaRed-60);
  position: absolute;
  top: 10px;
  right: 10px;
`;

export default PageAdminApplicationDetail;